/**
 * Performance plugin: collects long tasks, resource timing, and API latency.
 */
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

export function isInternalPixelRequest(
  rawUrl: string | undefined,
  apiBase: string,
  currentUrl?: string,
) {
  if (!rawUrl || !apiBase || !rawUrl.includes("/pxl/")) return false;

  const resolutionBase =
    currentUrl ||
    (typeof window !== "undefined"
      ? window.location.href
      : "https://example.com/");

  try {
    const requestUrl = new URL(rawUrl, resolutionBase);
    const apiUrl = new URL(apiBase, resolutionBase);

    return (
      requestUrl.origin === apiUrl.origin &&
      requestUrl.pathname.startsWith("/pxl/")
    );
  } catch {
    return false;
  }
}

function resolveFetchRequestMeta(
  input: RequestInfo | URL,
  init?: RequestInit,
): {
  method: string;
  rawUrl: string;
} {
  return {
    method:
      init?.method ||
      (typeof input !== "string" && !(input instanceof URL)
        ? input.method
        : "GET"),
    rawUrl:
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url,
  };
}

export function createPerfPlugin(ctx: PluginContext) {
  let longTaskObserver: PerformanceObserver | undefined;
  let resourceObserver: PerformanceObserver | undefined;
  let restoreFetch: (() => void) | undefined;
  let restoreXhr: (() => void) | undefined;
  let installed = false;
  let active = false;

  const emit = (type: string, props: Record<string, unknown>) => {
    if (!active) return;
    if (!ctx.canTrackCategory("performance_analytics")) return;
    ctx.track(type, props);
  };

  const hashUrl = (raw?: string) => {
    if (!raw) return undefined;
    const sanitized = ctx.redact.sanitizeUrl(raw);
    return sanitized ? ctx.redact.hashString(sanitized) : undefined;
  };

  const setupObservers = () => {
    if (typeof PerformanceObserver === "undefined") return;

    try {
      longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          emit("perf_longtask", {
            name: entry.name,
            duration_ms: Math.round(entry.duration),
            start_ms: Math.round(entry.startTime),
          });
        });
      });
      longTaskObserver.observe({ entryTypes: ["longtask"] });
    } catch {
      longTaskObserver = undefined;
    }

    try {
      resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const resource = entry as PerformanceResourceTiming;
          if (isInternalPixelRequest(resource.name, ctx.config.apiBase)) {
            return;
          }
          emit("perf_resource", {
            initiator_type: resource.initiatorType,
            duration_ms: Math.round(resource.duration),
            transfer_size: resource.transferSize || undefined,
            encoded_body_size: resource.encodedBodySize || undefined,
            next_hop_protocol: resource.nextHopProtocol || undefined,
            name_hash: hashUrl(resource.name),
          });
        });
      });
      resourceObserver.observe({ entryTypes: ["resource"] });
    } catch {
      resourceObserver = undefined;
    }
  };

  const patchFetch = () => {
    if (typeof window.fetch !== "function") return;
    const original = window.fetch;

    const installedFetch = async (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> => {
      const startedAt = performance.now();
      const { method, rawUrl } = resolveFetchRequestMeta(input, init);

      try {
        const response = await original.call(window, input, init);
        if (isInternalPixelRequest(rawUrl, ctx.config.apiBase)) {
          return response;
        }
        emit("perf_api", {
          transport: "fetch",
          method,
          status: response.status,
          duration_ms: Math.round(performance.now() - startedAt),
          url_hash: hashUrl(rawUrl),
          ok: response.ok,
        });
        return response;
      } catch (error) {
        if (isInternalPixelRequest(rawUrl, ctx.config.apiBase)) {
          throw error;
        }
        emit("perf_api", {
          transport: "fetch",
          method,
          duration_ms: Math.round(performance.now() - startedAt),
          url_hash: hashUrl(rawUrl),
          ok: false,
          error: error instanceof Error ? error.message : "fetch_error",
        });
        throw error;
      }
    };
    window.fetch = installedFetch;

    restoreFetch = () => {
      if (window.fetch === installedFetch) window.fetch = original;
    };
  };

  const patchXhr = () => {
    const proto = XMLHttpRequest.prototype as XMLHttpRequest & {
      __ppxMeta?: { method?: string; url?: string; startedAt?: number };
    };
    const open = proto.open;
    const send = proto.send;

    const installedOpen = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      (this as any).__ppxMeta = {
        method,
        url: typeof url === "string" ? url : url.toString(),
      };
      return open.call(
        this,
        method,
        url as string,
        async as boolean,
        username as string,
        password as string,
      );
    };

    const installedSend = function (
      this: XMLHttpRequest,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      const self = this as XMLHttpRequest & {
        __ppxMeta?: { method?: string; url?: string; startedAt?: number };
      };
      if (!self.__ppxMeta) self.__ppxMeta = {};
      self.__ppxMeta.startedAt = performance.now();

      const onDone = () => {
        self.removeEventListener("loadend", onDone);
        const meta = self.__ppxMeta;
        const duration = meta?.startedAt
          ? Math.round(performance.now() - meta.startedAt)
          : undefined;
        if (isInternalPixelRequest(meta?.url, ctx.config.apiBase)) {
          return;
        }
        emit("perf_api", {
          transport: "xhr",
          method: meta?.method || "GET",
          status: self.status,
          duration_ms: duration,
          url_hash: hashUrl(meta?.url),
          ok: self.status >= 200 && self.status < 400,
        });
      };

      self.addEventListener("loadend", onDone);
      return send.call(this, body as any);
    };
    proto.open = installedOpen;
    proto.send = installedSend;

    restoreXhr = () => {
      if (proto.open === installedOpen) proto.open = open;
      if (proto.send === installedSend) proto.send = send;
    };
  };

  return {
    onInit() {
      if (installed) return;
      installed = true;
      active = true;
      setupObservers();
      patchFetch();
      patchXhr();
    },
    onUnload() {
      active = false;
      longTaskObserver?.disconnect();
      resourceObserver?.disconnect();
      restoreFetch?.();
      restoreXhr?.();
    },
  };
}

registerPlugin("perf", createPerfPlugin);
